import { Controller } from '@hotwired/stimulus';
import * as outbox from '../utils/outbox';

// The strip under the header: says when the phone has no signal, and how many
// orders or messages are waiting on it. Sends them whenever there is signal:
// on load, when the phone comes back online, when the app is reopened, and
// every 20 seconds while anything waits. With signal, it also asks the
// service worker to save the pages this store will need with none.
export default class extends Controller {
  static targets = ['text'];

  connect() {
    this.update = () => this.render();
    this.online = () => {
      this.render();
      this.flush();
    };
    this.visible = () => document.visibilityState === 'visible' && this.flush();
    window.addEventListener('online', this.online);
    window.addEventListener('offline', this.update);
    window.addEventListener('outbox:changed', this.update);
    document.addEventListener('visibilitychange', this.visible);
    this.warmUp = () => this.warm();
    document.addEventListener('turbo:load', this.warmUp);
    // The service worker sent something in the background.
    this.fromWorker = (event) =>
      event.data?.type === 'outbox:changed' &&
      window.dispatchEvent(new CustomEvent('outbox:changed'));
    navigator.serviceWorker?.addEventListener('message', this.fromWorker);
    this.timer = setInterval(() => this.flush(), 20000);
    this.render();
    this.flush();
    this.warm();
  }

  disconnect() {
    window.removeEventListener('online', this.online);
    window.removeEventListener('offline', this.update);
    window.removeEventListener('outbox:changed', this.update);
    document.removeEventListener('visibilitychange', this.visible);
    document.removeEventListener('turbo:load', this.warmUp);
    navigator.serviceWorker?.removeEventListener('message', this.fromWorker);
    clearInterval(this.timer);
  }

  async flush() {
    if (!navigator.onLine) return;
    const waiting = (await outbox.all().catch(() => [])).filter(
      (e) => e.status === 'waiting',
    );
    if (waiting.length === 0) return;
    this.sending = true;
    this.render();
    await outbox.flush().catch(() => null);
    this.sending = false;
    this.render();
  }

  async render() {
    const entries = await outbox.all().catch(() => []);
    const waiting = entries.filter((e) => e.status === 'waiting').length;
    const refused = entries.filter((e) => e.status === 'refused').length;
    let text = '';
    let tone = '';
    if (!navigator.onLine) {
      text = 'No signal · working from this phone';
      if (waiting) text += ` · ${waiting} waiting to send`;
      tone = 'offline';
    } else if (this.sending || waiting) {
      text = `Signal found · sending ${waiting || ''}…`.replace('  ', ' ');
      tone = 'sending';
    } else if (refused) {
      text = `${
        refused === 1 ? 'Something' : `${refused} things`
      } didn't reach Bud's. Open it to see why.`;
      tone = 'refused';
    }
    this.element.hidden = !text;
    this.element.dataset.tone = tone;
    this.textTarget.textContent = text;
  }

  // Pages marked data-offline-warm list the URLs to save for no-signal use.
  async warm() {
    if (!navigator.onLine || !navigator.serviceWorker?.controller) return;
    const urls = [...document.querySelectorAll('[data-offline-warm]')].flatMap(
      (el) => JSON.parse(el.dataset.offlineWarm || '[]'),
    );
    if (urls.length)
      navigator.serviceWorker.controller.postMessage({ type: 'warm', urls });
  }
}
